// EmailRequestModal.js — Kuhrt 2026-07-07: shared email-send modal for Payment
// Requests and Funds Requests (mirrors InvoicePrep's EmailInvoiceModal). The server
// attaches the statement PDF, injects the reference/split table + signature, files
// the email + PDF on the claim, and audits. Props:
//   endpoint        POST target, e.g. `/payment-requests/12/email`
//   title           modal heading
//   defaultSubject  prefilled subject
//   attachmentLabel what the 📄 row says
//   recipientOptions optional [{id,label}] — FR per-payor statement selector
//   onClose / onSent
import { useEffect, useState } from "react";
import { apiFetch } from "../api";
import { EmailSentConfirmation } from "./ComposeEmailModal";

const IDENTITY_LABEL = { self: "My address", accounting: "accounting@waltersnorthwest.com", admin: "adminsupport@waltersnorthwest.com" };

export default function EmailRequestModal({ endpoint, title, defaultSubject, attachmentLabel,
                                            recipientOptions, onClose, onSent }) {
  const [status, setStatus] = useState(null);
  const [identity, setIdentity] = useState("self");
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState(defaultSubject || "");
  const [body, setBody] = useState("");
  const [recipientId, setRecipientId] = useState("");
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState(null);
  const [sentInfo, setSentInfo] = useState(null);

  useEffect(() => {
    apiFetch("/me/outlook/status").then(setStatus).catch(() => setStatus({ connected: false }));
  }, []);
  const ready = status && status.connected && status.email_send_enabled;

  const send = () => {
    if (!to.trim()) { setMsg({ type: "error", text: "Add at least one recipient." }); return; }
    setSending(true); setMsg(null);
    const esc = (x) => x.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    apiFetch(endpoint, { method: "POST", body: {
      to, cc, subject, body_html: esc(body).replace(/\n/g, "<br>"), sending_identity: identity,
      ...(recipientId ? { recipient_id: recipientId } : {}) } })
      .then(() => {
        setSentInfo({
          from: identity === "self" ? (status?.email || "my address") : (IDENTITY_LABEL[identity] || identity),
          to, cc, subject, body,
          attachments: [{ name: attachmentLabel }],
        });
        setSending(false);
      })
      .catch(e => { let d = "Email could not be sent."; try { d = JSON.parse(e.message).detail || d; } catch {} setMsg({ type: "error", text: d }); setSending(false); });
  };

  const overlay = { position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "48px 16px", overflowY: "auto" };
  const sheet = { background: "var(--surface2,#fff)", borderRadius: 12, width: "100%", maxWidth: 620, boxShadow: "0 10px 40px rgba(0,0,0,0.25)" };
  const lbl = { fontSize: 11, color: "var(--text3)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3, display: "block" };
  const inp = { width: "100%", boxSizing: "border-box" };

  if (sentInfo) {
    const finish = () => { onSent && onSent(); };
    return (
      <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) finish(); }}>
        <div style={sheet}>
          <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ margin: 0, fontSize: 16 }}>Email sent</h3>
            <button className="btn btn-outline btn-sm" onClick={finish}>✕</button>
          </div>
          <EmailSentConfirmation info={sentInfo} onDone={finish} />
        </div>
      </div>
    );
  }

  return (
    <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={sheet}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>{title}</h3>
          <button className="btn btn-outline btn-sm" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: 18 }}>
          {status && !status.connected && <div className="alert-error" style={{ marginBottom: 12 }}>Your Outlook isn't connected. Connect it in Profile → Outlook email before sending.</div>}
          {status && status.connected && !status.email_send_enabled && <div className="alert-error" style={{ marginBottom: 12 }}>Email sending isn't enabled for your account. Ask an administrator to enable it.</div>}
          {msg && <div className="alert-error" style={{ marginBottom: 12 }}>{msg.text}</div>}
          <div style={{ marginBottom: 10 }}>
            <label style={lbl}>From</label>
            <select value={identity} onChange={e => setIdentity(e.target.value)} style={inp} disabled={!ready}>
              {["self", ...(status?.shared_identities || [])].filter((v, i, a) => a.indexOf(v) === i).map(k => (
                <option key={k} value={k}>{IDENTITY_LABEL[k] || k}{k === "self" && status?.email ? ` (${status.email})` : ""}</option>))}
            </select>
          </div>
          {recipientOptions && recipientOptions.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <label style={lbl}>Statement</label>
              <select value={recipientId} onChange={e => setRecipientId(e.target.value)} style={inp} disabled={!ready}>
                <option value="">Full request — all payors</option>
                {recipientOptions.map(r => <option key={r.id} value={r.id}>{r.label} (their share only)</option>)}
              </select>
            </div>
          )}
          <div style={{ marginBottom: 10 }}>
            <label style={lbl}>To</label>
            <input value={to} onChange={e => setTo(e.target.value)} placeholder="ap@company.com, claims@underwriter.com" style={inp} disabled={!ready} />
          </div>
          <div style={{ marginBottom: 10 }}><label style={lbl}>Cc</label><input value={cc} onChange={e => setCc(e.target.value)} style={inp} disabled={!ready} /></div>
          <div style={{ marginBottom: 10 }}><label style={lbl}>Subject</label><input value={subject} onChange={e => setSubject(e.target.value)} style={inp} disabled={!ready} /></div>
          <div style={{ marginBottom: 10 }}><label style={lbl}>Message</label><textarea value={body} onChange={e => setBody(e.target.value)} rows={6} style={{ ...inp, resize: "vertical", fontFamily: "inherit" }} disabled={!ready} /></div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, background: "var(--bg3,#f8fafc)", padding: "6px 10px", borderRadius: 6 }}>
            <span>📄</span><span style={{ flex: 1 }}>{attachmentLabel} <span style={{ color: "var(--text3)" }}>(generated at send · exact copy filed on the claim)</span></span>
          </div>
          <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 8 }}>A reference table (request facts + payor split) is included in the body, then your signature. The email is filed on the claim (PHI-audited).</div>
        </div>
        <div style={{ padding: "12px 18px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="btn btn-outline btn-sm" onClick={onClose} disabled={sending}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={send} disabled={sending || !ready}>{sending ? "Sending…" : "Send Email"}</button>
        </div>
      </div>
    </div>
  );
}
